import { Transition, MantineTransition as MantineTransitionType, Box } from '@mantine/core';
import { forwardRef } from 'react';
import { useReducedMotion } from '../hooks';

export interface MantineTransitionProps {
  children: React.ReactNode;
  visible?: boolean;
  transition?: MantineTransitionType;
  duration?: number;
  timingFunction?: string;
  exitDuration?: number;
  keepMounted?: boolean;
  onEnter?: () => void;
  onEntered?: () => void;
  onExit?: () => void;
  onExited?: () => void;
}

/**
 * Wrapper around Mantine's Transition component with reduced motion support
 * Built-in transitions: fade, scale, scale-y, scale-x, skew-up, skew-down,
 * rotate-left, rotate-right, slide-down, slide-up, slide-left, slide-right, pop
 */
const MantineTransition = forwardRef<HTMLDivElement, MantineTransitionProps>(
  (
    {
      children,
      visible = true,
      transition = 'fade',
      duration = 200,
      timingFunction = 'ease',
      exitDuration,
      keepMounted = false,
      onEnter,
      onEntered,
      onExit,
      onExited,
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();
    const actualDuration = reducedMotion ? 0 : duration;
    const actualExitDuration = reducedMotion ? 0 : (exitDuration ?? duration);

    return (
      <Transition
        mounted={visible}
        transition={transition}
        duration={actualDuration}
        exitDuration={actualExitDuration}
        timingFunction={timingFunction}
        keepMounted={keepMounted}
        onEnter={onEnter}
        onEntered={onEntered}
        onExit={onExit}
        onExited={onExited}
      >
        {(styles) => (
          <Box ref={ref} style={styles}>
            {children}
          </Box>
        )}
      </Transition>
    );
  }
);

MantineTransition.displayName = 'MantineTransition';

export default MantineTransition;
